import { Button, Grid, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import {
  COST_SUMMARY_KINDS_OPTIONS,
  COST_SUMMARY_TYPES_OPTIONS,
  SUB_RESOURCE_LIST_OPTIONS,
} from 'pages/admin/CostDashboard/constants';
import { useGetResourceList, useGetRollupNamespaces } from 'pages/admin/CostDashboard/hooks';
import { IUseGetPeriodicCostRecords } from 'pages/admin/CostDashboard/hooks/useGetPeriodicCostRecords';
import { useEffect, useMemo } from 'react';
import { DateRange } from 'react-day-picker';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { DateRangePicker } from 'shared/components/Date';
import { FormSelectField, FormTextFieldBase } from 'shared/components/form';
import { PeriodicCostRecordsRequest } from 'shared/types/protoc-gen/bffbillsystem';

interface ICostDetailsFilter extends IUseGetPeriodicCostRecords {
  setCostDetailsFilter: React.Dispatch<React.SetStateAction<IUseGetPeriodicCostRecords>>;
  namespaceType: string;
}

interface CostDetailsFilterSchema extends PeriodicCostRecordsRequest {
  dateRange: DateRange;
}

const CostDetailsFilter: React.FC<ICostDetailsFilter> = ({
  billingUnitId,
  endTime,
  kind,
  namespace,
  namespaceType,
  resourceName,
  setCostDetailsFilter,
  startTime,
  subresource,
  type,
}) => {
  const { data: namespaceList } = useGetRollupNamespaces({ type: namespaceType });

  const form = useForm<CostDetailsFilterSchema>({
    defaultValues: {
      dateRange: {
        from: dayjs(startTime).toDate(),
        to: dayjs(endTime).toDate(),
      },
      namespace,
      resourceName,
      kind,
      type,
      billingUnitId,
      subresource,
    },
  });

  const { control } = form;

  const namespaceForm = useWatch({ control, name: 'namespace' });
  const kindForm = useWatch({ control, name: 'kind' });
  const typeForm = useWatch({ control, name: 'type' });

  const { data: resourcesList } = useGetResourceList({
    namespace: namespaceForm,
    kind: kindForm,
    type: typeForm,
    startTime,
    endTime,
    subresource,
  });

  useEffect(() => {
    if (namespaceList?.namespace?.length && !form.getValues('namespace')) {
      form.setValue('namespace', namespaceList.namespace[0]);
    }
  }, [namespaceList, form]);

  const handleApply = () => {
    const values = form.getValues();

    const { from, to } = values.dateRange || {};

    setCostDetailsFilter({
      startTime: from ? dayjs(from).format('YYYY-MM-DD') : '',
      endTime: to ? dayjs(to).format('YYYY-MM-DD') : '',
      kind: values.kind,
      type: values.type,
      subresource: values.subresource,
      billingUnitId: values.billingUnitId,
      resourceName: values.resourceName,
      namespace: values.namespace,
    });
  };

  const namespaceListOptions = useMemo(() => {
    if (!namespaceList) return [];

    return [
      ...namespaceList.namespace.map(namespace => ({
        label: namespace,
        value: namespace,
      })),
    ];
  }, [namespaceList]);

  const resourcesOptions = useMemo(() => {
    if (!resourcesList) return [];

    return [
      { label: 'All resources', value: 'ALL' },
      ...resourcesList.list.map(resource => ({
        label: resource,
        value: resource,
      })),
    ];
  }, [resourcesList]);

  return (
    <FormProvider {...form}>
      <form>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Stack>
              <Typography>Namespace</Typography>

              <FormSelectField fieldConfig={{ name: 'namespace' }} items={namespaceListOptions} />
            </Stack>
          </Grid>

          <Grid item xs={4}>
            <Stack>
              <Typography>Kind</Typography>

              <FormSelectField
                fieldConfig={{ name: 'kind' }}
                items={COST_SUMMARY_KINDS_OPTIONS.slice(1)}
              />
            </Stack>
          </Grid>

          <Grid item xs={4}>
            <Stack>
              <Typography>Type</Typography>

              <FormSelectField
                fieldConfig={{ name: 'type' }}
                items={COST_SUMMARY_TYPES_OPTIONS.slice(1)}
              />
            </Stack>
          </Grid>

          <Grid item xs={4}>
            <Stack>
              <Typography>Sub-Resource</Typography>

              <FormSelectField
                fieldConfig={{ name: 'subresource' }}
                items={SUB_RESOURCE_LIST_OPTIONS}
              />
            </Stack>
          </Grid>

          <Grid item xs={4}>
            <Stack>
              <Typography>Resource Name</Typography>

              <FormSelectField fieldConfig={{ name: 'resourceName' }} items={resourcesOptions} />
            </Stack>
          </Grid>

          <Grid item xs={4}>
            <Stack>
              <Typography>Billing Unit ID</Typography>

              <FormTextFieldBase
                fieldConfig={{
                  name: 'billingUnitId',
                  label: 'Billing Unit ID',
                  placeholder: 'Enter billing unit ID',
                }}
              />
            </Stack>
          </Grid>

          <Grid item xs={4}>
            <Stack>
              <Typography>Date Range</Typography>

              <DateRangePicker
                onChange={range => form.setValue('dateRange', range)}
                value={form.watch('dateRange')}
              />
            </Stack>
          </Grid>

          <Grid
            item
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
              p: 2,
            }}
            xs={8}
          >
            <Button onClick={handleApply}>Apply</Button>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default CostDetailsFilter;
